import { FC } from 'react'

import { Heading } from '~/components/common/heading'
import { Section } from '~/components/common/section'
import { Container } from '~/components/layout/container'
import { Link } from '~/components/primitives/link'

import s from '../legal.module.scss'

export const Policy: FC = () => {
  return (
    <Section className={s.section}>
      <Container size="sm" className={s.container}>
        <div className={s.heading}>
          <Heading as="h1" size="sm">
            Privacy Policy
          </Heading>
          <span>Effective date: 15 May 2025</span>
        </div>

        <div className={s.content}>
          <p>
            Welcome to Ride the Bus (referred to as "we", "our", or "us"). Your
            privacy is important to us. This Privacy Policy explains how we
            collect, use, and protect your personal information when you
            interact with our website, game, and related services (collectively,
            the "Service").
            <br />
            <br />
            By using our Service, you agree to the terms of this Privacy Policy.
            If you do not agree, please discontinue use of the Service.
            <br />
            <br />
            If you have a disability, you may access this Privacy Policy in an
            alternative format by contacting{' '}
            <Link href="https://t.me/ride_the_bus">@ride_the_bus</Link>.
          </p>
        </div>

        <Heading as="h2">Information We Collect</Heading>
        <div className={s.content}>
          <p className={s.downPadding}>
            We may collect the following types of information:
          </p>

          <Heading as="h3">Information You Provide to Us</Heading>
          <ul>
            <li>
              <strong>Wallet Address:</strong> When you connect your wallet
              (e.g., MetaMask, Coinbase Wallet) to interact with the Service.
            </li>
            <li>
              <strong>Contact Information:</strong> If you provide us with your
              email or other details for newsletters, support, or promotions.
            </li>
            <li>
              <strong>Support Requests:</strong> Information you provide when
              you request help from our support team.
            </li>
          </ul>

          <Heading as="h3">Information We Collect Automatically</Heading>
          <ul>
            <li>
              <strong>Usage Data:</strong> Interactions with our Service, such
              as page views, time spent, and clicks.
            </li>
            <li>
              <strong>Device Information:</strong> Browser type, device type,
              and operating system.
            </li>
            <li>
              <strong>Log Data:</strong> IP addresses and other network
              identifiers.
            </li>
          </ul>

          <Heading as="h3">Blockchain Data</Heading>
          <p>
            Since NFT and gaming interactions occur on public blockchains, any
            transactions made through smart contracts are publicly available and
            cannot be deleted. This may include your wallet address, transaction
            details, and metadata related to the NFT or in-game assets.
          </p>
        </div>

        <Heading as="h2">How We Use Your Information</Heading>
        <div className={s.content}>
          <p>We use the information collected for the following purposes:</p>
          <ul>
            <li>
              <strong>To Operate the Service:</strong> Enable you to play the
              game, manage NFTs, process transactions, and track game progress.
            </li>
            <li>
              <strong>To Improve Our Service:</strong> Monitor usage, identify
              bugs, and enhance user experience.
            </li>
            <li>
              <strong>To Communicate With You:</strong> Send updates, game news,
              promotional offers, or support responses.
            </li>
            <li>
              <strong>To Ensure Security:</strong> Detect fraud, prevent abuse,
              and secure our platform.
            </li>
          </ul>
          <p>We do not sell your personal information to third parties.</p>
        </div>

        <Heading as="h2">How We Share Your Information</Heading>
        <div className={s.content}>
          <p>
            We may share your information only in the following circumstances:
          </p>
          <ul>
            <li>
              <strong>Service Providers:</strong> Third-party services that help
              us operate the game (e.g., analytics, payment processing).
            </li>
            <li>
              <strong>Legal Requirements:</strong> If required to comply with a
              legal obligation, subpoena, or government request.
            </li>
            <li>
              <strong>Business Transfers:</strong> If we merge, sell, or
              transfer assets as part of a business change.
            </li>
          </ul>
        </div>

        <Heading as="h2">How We Protect Your Information</Heading>
        <div className={s.content}>
          <p>
            We take reasonable security measures to protect your information
            from unauthorized access, theft, and misuse. However, since
            blockchain data is public and immutable, certain information (like
            wallet addresses and NFT transactions) cannot be removed or altered.
            <br />
            <br />
            We also encourage you to protect your wallet credentials, as we do
            not have access to your private keys.
          </p>
        </div>

        <Heading as="h2">Cookies and Tracking</Heading>
        <div className={s.content}>
          <p>
            We may use cookies or similar tracking technologies to improve the
            Service. Cookies help us understand user preferences, track site
            usage, and enhance your experience.
          </p>
        </div>

        <Heading as="h2">Children's Privacy</Heading>
        <div className={s.content}>
          <p>
            Our Service is not intended for children under 18. We do not
            knowingly collect personal information from children. If we discover
            that we have collected information from a child, we will delete it.
          </p>
        </div>

        <Heading as="h2">Changes to This Privacy Policy</Heading>
        <div className={s.content}>
          <p>
            We may update this Privacy Policy from time to time. Any changes
            will be posted on this page with the "Last Updated" date at the top.
            Your continued use of the Service after any changes signifies your
            acceptance of the updated policy.
          </p>
        </div>

        <Heading as="h2">Contact Us</Heading>
        <div className={s.content}>
          <p>
            If you have questions or concerns about this Privacy Policy or how
            we handle your information, please contact us at{' '}
            <Link href="https://t.me/ride_the_bus">@ride_the_bus</Link>.
          </p>
          <p className={s.footer}>
            This Privacy Policy is designed to provide transparency and ensure
            your confidence in our commitment to protecting your privacy. Thank
            you for being part of Ride the Bus!
          </p>
        </div>
      </Container>
    </Section>
  )
}
